'use client'

/**
 * UnifiedCalendar — one page, four views. Toggle between Today (all three merged), Economic, Earnings and
 * Dividends. The Earnings and Dividends tabs reuse the existing polished dashboards; Economic and Today
 * render inline. Keeps the three calendars in one place so users stop hunting across separate pages.
 */

import { useState } from 'react'
import Link from 'next/link'
import { CalendarDays, Landmark, Building2, Coins } from 'lucide-react'
import type { EconDay } from '@/lib/dashboard/economicCalendar'
import type { EarningsWeek } from '@/lib/dashboard/earningsCalendar'
import type { DividendsPayload } from '@/lib/dashboard/dividends'
import type { TodayMarket } from '@/lib/dashboard/marketCalendar'
import { readEconEvent, type EconRead, type VerdictTone, type DeltaTone } from '@/lib/dashboard/econEventRead'
import EarningsCalendarDashboard from './EarningsCalendarDashboard'
import DividendsDashboard from './DividendsDashboard'

type View = 'today' | 'economic' | 'earnings' | 'dividends'
const TABS: { key: View; label: string; Icon: typeof CalendarDays }[] = [
  { key: 'today', label: 'Today', Icon: CalendarDays },
  { key: 'economic', label: 'Economic', Icon: Landmark },
  { key: 'earnings', label: 'Earnings', Icon: Building2 },
  { key: 'dividends', label: 'Dividends', Icon: Coins },
]

export default function UnifiedCalendar({ today, econ, earnings, dividends }: { today: TodayMarket; econ: EconDay[]; earnings: EarningsWeek; dividends: DividendsPayload }) {
  const [view, setView] = useState<View>('today')

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map((t) => {
          const active = view === t.key
          return (
            <button key={t.key} onClick={() => setView(t.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${active ? 'border-amber-500/60 bg-amber-500/15 text-amber-200' : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'}`}>
              <t.Icon className="h-3.5 w-3.5" />{t.label}
            </button>
          )
        })}
      </div>

      {view === 'today' && <TodayView today={today} onJump={setView} />}
      {view === 'economic' && <EconomicView days={econ} />}
      {view === 'earnings' && <EarningsCalendarDashboard initial={earnings} />}
      {view === 'dividends' && <DividendsDashboard initial={dividends} />}
    </div>
  )
}

const whenLabel = (w: 'bmo' | 'amc' | 'tbd') => (w === 'bmo' ? 'Before open' : w === 'amc' ? 'After close' : 'Time TBD')

function TodayView({ today, onJump }: { today: TodayMarket; onJump: (v: View) => void }) {
  if (today.total === 0) {
    return <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-12 text-center text-sm text-zinc-500">No scheduled market events today. Check the other tabs for the week ahead.</div>
  }
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card title="Economic" Icon={Landmark} count={today.econ.length} onMore={() => onJump('economic')}>
        {today.econ.length ? today.econ.map((e, i) => {
          const read = readEconEvent(e)
          return (
            <div key={i} tabIndex={read ? 0 : undefined}
              className={`relative flex items-center gap-2 py-1 text-[13px] outline-none ${read ? 'group/ht -mx-1 cursor-help rounded-md px-1 transition-colors hover:bg-zinc-800/40 focus-visible:bg-zinc-800/40' : ''}`}>
              <span className="w-14 shrink-0 text-[11px] tabular-nums text-zinc-500">{e.timeEt}</span>
              <span className="flex min-w-0 flex-1 items-center text-zinc-200">{e.event}{read && <ReadDot />}</span>
              {e.released && <span className="shrink-0 rounded bg-zinc-700/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-zinc-400">out</span>}
              {read && <EconResultCard e={e} read={read} />}
            </div>
          )
        }) : <Empty>No macro releases today.</Empty>}
      </Card>

      <Card title="Earnings" Icon={Building2} count={today.earningsCount} onMore={() => onJump('earnings')}>
        {today.earnings.length ? (
          <div className="flex flex-wrap gap-1.5 py-1">
            {today.earnings.map((r) => (
              <Link key={r.symbol} href={`/stocks/${encodeURIComponent(r.symbol)}`} target="_blank" rel="noopener noreferrer" title={`${r.name} - ${whenLabel(r.when)}`} className="rounded-md bg-sky-500/10 px-2 py-1 text-[12px] font-bold text-sky-300 hover:bg-sky-500/20">${r.symbol}</Link>
            ))}
            {today.earningsCount > today.earnings.length && <span className="self-center px-1 text-[12px] text-zinc-500">+{today.earningsCount - today.earnings.length} more</span>}
          </div>
        ) : <Empty>No earnings today.</Empty>}
      </Card>

      <Card title="Ex-dividend" Icon={Coins} count={today.dividendsCount} onMore={() => onJump('dividends')}>
        {today.dividends.length ? (
          <div className="flex flex-wrap gap-1.5 py-1">
            {today.dividends.map((d) => (
              <Link key={d.symbol} href={`/stocks/${encodeURIComponent(d.symbol)}`} target="_blank" rel="noopener noreferrer" title={`${d.name} - $${d.amount.toFixed(2)}/sh${d.yieldPct ? `, ${d.yieldPct.toFixed(1)}% yield` : ''}`} className="rounded-md bg-emerald-500/10 px-2 py-1 text-[12px] font-bold text-emerald-300 hover:bg-emerald-500/20">${d.symbol}</Link>
            ))}
            {today.dividendsCount > today.dividends.length && <span className="self-center px-1 text-[12px] text-zinc-500">+{today.dividendsCount - today.dividends.length} more</span>}
          </div>
        ) : <Empty>No ex-dividends today.</Empty>}
      </Card>
    </div>
  )
}

function EconomicView({ days }: { days: EconDay[] }) {
  if (!days.length) return <Empty>No upcoming economic events.</Empty>
  return (
    <div className="space-y-4">
      {days.map((d) => (
        <div key={d.date}>
          <div className="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-zinc-400">{d.label}</div>
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
            {d.events.map((e, i) => {
              const read = readEconEvent(e)
              return (
                <div key={i} tabIndex={read ? 0 : undefined}
                  className={`relative flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13px] outline-none ${i > 0 ? 'border-t border-zinc-800/60' : ''} ${read ? 'group/ht cursor-help transition-colors hover:bg-zinc-800/40 focus-visible:bg-zinc-800/40' : ''}`}>
                  <span className="w-16 shrink-0 text-[11px] tabular-nums text-zinc-500">{e.timeEt}</span>
                  <span className="flex min-w-0 flex-1 items-center font-semibold text-zinc-100">{e.event}{read && <ReadDot />}</span>
                  {e.actual != null && <Stat label="Act" value={e.actual} tone="actual" />}
                  {e.consensus != null && <Stat label="Est" value={e.consensus} />}
                  {e.previous != null && <Stat label="Prev" value={e.previous} />}
                  {read && <EconResultCard e={e} read={read} />}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function Card({ title, Icon, count, onMore, children }: { title: string; Icon: typeof CalendarDays; count: number; onMore: () => void; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/30 p-4">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-amber-400" />
        <h2 className="text-sm font-bold text-white">{title}</h2>
        <span className="text-[11px] font-semibold text-zinc-500">{count}</span>
        <button onClick={onMore} className="ml-auto text-[11px] font-semibold text-amber-400 hover:text-amber-300">View all →</button>
      </div>
      {children}
    </div>
  )
}
function Stat({ label, value, tone }: { label: string; value: string; tone?: 'actual' }) {
  return <span className="shrink-0 text-[11px] tabular-nums"><span className="text-zinc-600">{label} </span><span className={tone === 'actual' ? 'font-bold text-amber-300' : 'text-zinc-300'}>{value}</span></span>
}
function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-2 text-[12px] text-zinc-500">{children}</div>
}

// ── Economic-results hover summary (owner 2026-09-06). CSS-only reveal (group/ht + hover / focus-within)
//    so it works on desktop hover AND mobile tap (tabIndex makes the row focusable). The read itself -
//    direction-aware verdict, vs-estimate / vs-previous, plain-English + market context - is computed in
//    readEconEvent; this is pure presentation. ──
const VERDICT_CLS: Record<VerdictTone, string> = {
  up: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  cool: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  warm: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  hot: 'border-rose-500/35 bg-rose-500/12 text-rose-300',
  down: 'border-rose-500/35 bg-rose-500/12 text-rose-300',
  flat: 'border-zinc-700 bg-zinc-800/60 text-zinc-300',
}
const DELTA_CLS: Record<DeltaTone, string> = { up: 'text-emerald-400', dn: 'text-rose-400', flat: 'text-zinc-500' }

/** The little amber "i" that signals a row carries a hover/tap summary. */
function ReadDot() {
  return <span className="ml-1.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-amber-500/40 text-[8.5px] font-bold leading-none text-amber-400/90" aria-hidden>i</span>
}

type EconLike = { event: string; timeEt?: string; actual: string | null; consensus: string | null; previous: string | null; released?: boolean }

function EconResultCard({ e, read }: { e: EconLike; read: EconRead }) {
  return (
    <div className="pointer-events-none absolute left-0 top-full z-50 mt-1.5 block w-[360px] max-w-[calc(100vw-2.5rem)] -translate-y-1 rounded-xl border border-zinc-700 bg-zinc-950 p-3.5 text-left opacity-0 shadow-2xl shadow-black/70 transition-all duration-150 invisible group-hover/ht:visible group-hover/ht:translate-y-0 group-hover/ht:opacity-100 group-focus-within/ht:visible group-focus-within/ht:translate-y-0 group-focus-within/ht:opacity-100">
      <div className="mb-2.5 flex items-baseline gap-2">
        <span className="text-[13px] font-bold text-white">{e.event}</span>
        {e.timeEt && <span className="font-mono text-[10.5px] text-zinc-500">{e.timeEt} ET</span>}
        <span className="ml-auto shrink-0 rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[8.5px] font-bold uppercase text-emerald-400">out</span>
      </div>
      <div className="mb-3 flex items-center gap-2.5">
        <span className="font-mono text-[24px] font-extrabold leading-none text-white tabular-nums">{e.actual}</span>
        <span className={`rounded-lg border px-2 py-1 text-[11.5px] font-bold ${VERDICT_CLS[read.verdict.tone]}`}>{read.verdict.label}</span>
      </div>
      {(read.vsEst || read.vsPrev) && (
        <div className="mb-2.5 grid grid-cols-2 gap-2">
          {read.vsEst && <CompareBox title={`vs estimate${e.consensus ? ` (${e.consensus})` : ''}`} c={read.vsEst} />}
          {read.vsPrev && <CompareBox title={`vs previous${e.previous ? ` (${e.previous})` : ''}`} c={read.vsPrev} />}
        </div>
      )}
      <p className="border-t border-zinc-800 pt-2.5 text-[12px] leading-relaxed text-zinc-200">{read.plain}</p>
      <p className="mt-2 text-[11.5px] leading-relaxed text-zinc-500"><span className="font-semibold text-zinc-400">What it is: </span>{read.glossary}</p>
      {read.marketRead && (
        <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-dashed border-zinc-700 bg-zinc-900/60 px-2.5 py-2">
          <span className="shrink-0 rounded border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 text-[8.5px] font-bold uppercase text-sky-300">Market read</span>
          <span className="text-[11.5px] leading-relaxed text-zinc-400">{read.marketRead}</span>
        </div>
      )}
    </div>
  )
}

function CompareBox({ title, c }: { title: string; c: { label: string; delta: string | null; tone: DeltaTone } }) {
  const showDelta = c.delta && c.delta !== '0' && c.delta !== '0.0pp'
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 px-2.5 py-1.5">
      <div className="text-[9px] font-bold uppercase tracking-wide text-zinc-500">{title}</div>
      <div className="font-mono text-[12.5px] font-bold text-zinc-200 tabular-nums">{c.label}{showDelta && <span className={`ml-1.5 ${DELTA_CLS[c.tone]}`}>{c.delta}</span>}</div>
    </div>
  )
}
